import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { User, AuthContextType } from '../types/auth';
import { authApi, sendAuthAudit } from '../services/api';
import {
  getToken,
  setToken,
  removeToken,
  getCurrentUser,
  hasRole as utilHasRole,
  parseToken,
  getTokenExpirationSeconds
} from '../utils/auth';
import TokenExpirationModal from '../components/common/TokenExpirationModal';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const WARNING_BEFORE_EXPIRY_SECONDS = 5 * 60; // 5 minutes
const AUTO_REFRESH_BEFORE_EXPIRY_SECONDS = 10 * 60; // 10 minutes – proactive refresh

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('Initializing auth...');
        const storedToken = getToken();
        if (storedToken) {
          console.log('Found stored token');
          const currentUser = getCurrentUser();
          if (currentUser) {
            console.log('User from token:', currentUser.email, 'Roles:', currentUser.roles);
            setTokenState(storedToken);

            // Load full profile from backend to get preferences and colors
            try {
              const profile = await authApi.getProfile();
              console.log('Loaded profile from backend:', profile);
              // Merge token data with profile data
              const fullUser = {
                ...currentUser,
                preferredSportId: profile.preferredSportId,
                preferredClubId: profile.preferredClubId,
                homeMatchColor: profile.homeMatchColor,
                awayMatchColor: profile.awayMatchColor,
                isMicrosoftAccount: profile.isMicrosoftAccount,
                preferredViewMode: profile.preferredViewMode,
                showDrivingTimes: profile.showDrivingTimes
              };
              setUser(fullUser);
            } catch (profileError) {
              console.error('Error loading profile:', profileError);
              // Fall back to token data
              setUser(currentUser);
            }
          } else {
            console.log('Token is invalid or expired, clearing');
            // Token is invalid, clear it
            removeToken();
            setTokenState(null);
            setUser(null);
          }
        } else {
          console.log('No stored token found');
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        removeToken();
        setTokenState(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Handle URL parameters for OAuth callback (Google/Microsoft)
  useEffect(() => {
    const handleOAuthCallback = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      const redirectPath = urlParams.get('redirect');

      if (tokenFromUrl) {
        console.log('OAuth callback: Token received from URL');

        // Store token in localStorage first
        setToken(tokenFromUrl);

        // Parse token directly (don't rely on getCurrentUser which reads from localStorage)
        const userFromToken = parseToken(tokenFromUrl);
        if (userFromToken) {
          console.log('OAuth callback: User parsed from token:', userFromToken.email, 'Roles:', userFromToken.roles);

          // Update state
          setTokenState(tokenFromUrl);

          // Load full profile from backend
          (async () => {
            try {
              const profile = await authApi.getProfile();
              const fullUser = {
                ...userFromToken,
                preferredSportId: profile.preferredSportId,
                preferredClubId: profile.preferredClubId,
                homeMatchColor: profile.homeMatchColor,
                awayMatchColor: profile.awayMatchColor,
                isMicrosoftAccount: profile.isMicrosoftAccount,
                preferredViewMode: profile.preferredViewMode,
                showDrivingTimes: profile.showDrivingTimes
              };
              setUser(fullUser);
            } catch (profileError) {
              console.error('Error loading profile:', profileError);
              setUser(userFromToken);
            } finally {
              setIsLoading(false);
            }
          })();
        } else {
          console.error('OAuth callback: Failed to parse user from token');
          removeToken();
          setTokenState(null);
          setUser(null);
          setIsLoading(false);
        }

        // Clean up URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        // Store redirect path for later use
        if (redirectPath && redirectPath !== '/' && redirectPath !== '/login') {
          sessionStorage.setItem('oauth_redirect', redirectPath);
        }
      }
    };

    handleOAuthCallback();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await authApi.login({ email, password });

      setToken(response.token);
      setTokenState(response.token);

      const userFromToken = getCurrentUser();
      if (userFromToken) {
        // Load full profile from backend
        try {
          const profile = await authApi.getProfile();
          const fullUser = {
            ...userFromToken,
            preferredSportId: profile.preferredSportId,
            preferredClubId: profile.preferredClubId,
            homeMatchColor: profile.homeMatchColor,
            awayMatchColor: profile.awayMatchColor,
            isMicrosoftAccount: profile.isMicrosoftAccount,
            preferredViewMode: profile.preferredViewMode,
            showDrivingTimes: profile.showDrivingTimes
          };
          setUser(fullUser);
        } catch (profileError) {
          console.error('Error loading profile:', profileError);
          setUser(userFromToken);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, displayName?: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await authApi.register({ email, password, displayName });

      setToken(response.token);
      setTokenState(response.token);

      const userFromToken = getCurrentUser();
      if (userFromToken) {
        // Load full profile from backend
        try {
          const profile = await authApi.getProfile();
          const fullUser = {
            ...userFromToken,
            preferredSportId: profile.preferredSportId,
            preferredClubId: profile.preferredClubId,
            homeMatchColor: profile.homeMatchColor,
            awayMatchColor: profile.awayMatchColor,
            isMicrosoftAccount: profile.isMicrosoftAccount,
            preferredViewMode: profile.preferredViewMode,
            showDrivingTimes: profile.showDrivingTimes
          };
          setUser(fullUser);
        } catch (profileError) {
          console.error('Error loading profile:', profileError);
          setUser(userFromToken);
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // --- Token expiration timer logic ---
  const clearExpirationTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (autoRefreshTimerRef.current) {
      clearTimeout(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
  }, []);

  const logout = useCallback((): void => {
    clearExpirationTimers();
    setShowExpirationModal(false);
    removeToken();
    setTokenState(null);
    setUser(null);
  }, [clearExpirationTimers]);

  // Listen for session-expired events from the Axios 401 interceptor.
  // This ensures React state is cleared when the backend rejects a token,
  // preventing "zombie sessions" where the UI thinks the user is logged in
  // but all API calls fail silently.
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn('Session expired (401 from backend) – logging out.');
      sendAuthAudit('FRONTEND_SESSION_EXPIRED', '401 interceptor triggered logout');
      logout();
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [logout]);

  // Listen for successful token refreshes from the Axios 401 interceptor
  // so AuthContext state stays in sync with the new token.
  useEffect(() => {
    const handleTokenRefreshed = (e: Event) => {
      const customEvent = e as CustomEvent<{ token: string }>;
      const newToken = customEvent.detail?.token;
      if (newToken) {
        console.log('Token refreshed by interceptor – updating AuthContext state.');
        setTokenState(newToken);
        const userFromToken = parseToken(newToken);
        if (userFromToken) {
          setUser(prev => prev ? { ...prev, ...userFromToken } : userFromToken);
        }
      }
    };
    window.addEventListener('auth:token-refreshed', handleTokenRefreshed);
    return () => window.removeEventListener('auth:token-refreshed', handleTokenRefreshed);
  }, []);

  const startCountdown = useCallback((initialSeconds: number) => {
    setCountdownSeconds(initialSeconds);
    setShowExpirationModal(true);

    countdownIntervalRef.current = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev <= 1) {
          // Time's up – auto-logout
          sendAuthAudit('TOKEN_COUNTDOWN_EXPIRED', 'User did not respond to expiration modal');
          clearExpirationTimers();
          setShowExpirationModal(false);
          removeToken();
          setTokenState(null);
          setUser(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearExpirationTimers]);

  // Proactive silent token refresh – runs before the warning modal appears
  const doSilentRefresh = useCallback(async () => {
    try {
      console.log('Proactive token refresh – refreshing silently...');
      const response = await authApi.refreshToken();
      setToken(response.token);
      setTokenState(response.token);
      const userFromToken = parseToken(response.token);
      if (userFromToken) {
        setUser(prev => prev ? { ...prev, ...userFromToken } : userFromToken);
      }
      setShowExpirationModal(false);
      console.log('Token silently refreshed successfully');
    } catch (error) {
      console.warn('Silent token refresh failed – user will see expiration modal', error);
      sendAuthAudit('SILENT_REFRESH_FAILED', String(error));
      // Don't logout here – the warning modal / countdown will handle it
    }
  }, []);

  const scheduleExpirationWarning = useCallback((currentToken: string) => {
    clearExpirationTimers();
    const remaining = getTokenExpirationSeconds(currentToken);
    if (remaining <= 0) return;

    // Schedule proactive silent refresh (at AUTO_REFRESH_BEFORE_EXPIRY_SECONDS before expiry)
    if (remaining > AUTO_REFRESH_BEFORE_EXPIRY_SECONDS) {
      const autoRefreshDelay = (remaining - AUTO_REFRESH_BEFORE_EXPIRY_SECONDS) * 1000;
      autoRefreshTimerRef.current = setTimeout(() => {
        doSilentRefresh();
      }, autoRefreshDelay);
    } else if (remaining > WARNING_BEFORE_EXPIRY_SECONDS) {
      // Already within auto-refresh window but not yet in warning window – refresh now
      doSilentRefresh();
    }

    if (remaining <= WARNING_BEFORE_EXPIRY_SECONDS) {
      // Already within the warning window – start countdown immediately
      startCountdown(remaining);
    } else {
      // Schedule the warning for (remaining - WARNING_BEFORE_EXPIRY_SECONDS) seconds from now
      const delayMs = (remaining - WARNING_BEFORE_EXPIRY_SECONDS) * 1000;
      warningTimerRef.current = setTimeout(() => {
        startCountdown(WARNING_BEFORE_EXPIRY_SECONDS);
      }, delayMs);
    }
  }, [clearExpirationTimers, startCountdown, doSilentRefresh]);

  // Set up the expiration timer whenever the token changes
  useEffect(() => {
    if (token) {
      scheduleExpirationWarning(token);
    } else {
      clearExpirationTimers();
      setShowExpirationModal(false);
    }
    return () => clearExpirationTimers();
  }, [token, scheduleExpirationWarning, clearExpirationTimers]);

  const handleStayLoggedIn = async () => {
    try {
      const response = await authApi.refreshToken();
      // Store the new token
      setToken(response.token);
      setTokenState(response.token);

      // Update user from the new token
      const userFromToken = parseToken(response.token);
      if (userFromToken) {
        // Merge with existing profile data
        setUser(prev => prev ? { ...prev, ...userFromToken } : userFromToken);
      }

      // Hide modal and schedule the next warning
      setShowExpirationModal(false);
      clearExpirationTimers();
      scheduleExpirationWarning(response.token);

      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Error refreshing token:', error);
      sendAuthAudit('MANUAL_REFRESH_FAILED', String(error));
      // If refresh fails, log out
      logout();
    }
  };

  // Refresh user data from the backend (e.g., after profile update)
  const refreshUser = async (): Promise<void> => {
    const storedToken = getToken();
    if (!storedToken) return;

    const userFromToken = getCurrentUser();
    if (!userFromToken) return;

    try {
      const profile = await authApi.getProfile();
      const fullUser = {
        ...userFromToken,
        preferredSportId: profile.preferredSportId,
        preferredClubId: profile.preferredClubId,
        homeMatchColor: profile.homeMatchColor,
        awayMatchColor: profile.awayMatchColor,
        isMicrosoftAccount: profile.isMicrosoftAccount,
        preferredViewMode: profile.preferredViewMode,
        showDrivingTimes: profile.showDrivingTimes
      };
      setUser(fullUser);
      console.log('User refreshed with updated profile:', fullUser);
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const hasRole = (role: string): boolean => {
    return utilHasRole(user, role);
  };

  const isAuthenticated = Boolean(token && user);

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated,
    isLoading,
    hasRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showExpirationModal && (
        <TokenExpirationModal
          remainingSeconds={countdownSeconds}
          onStayLoggedIn={handleStayLoggedIn}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
